import React, { useState, useMemo } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Toolbar,
  Box,
  Typography,
  useTheme,
  Collapse,
  IconButton,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  TipsAndUpdates as ChatIcon,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import FrameworksIcon from "@mui/icons-material/AccountBalance";
import PolicyIcon from "@mui/icons-material/Gavel";
import DocumentsIcon from "@mui/icons-material/Description";
import RiskIcon from "@mui/icons-material/CrisisAlert";
import AccessIcon from "@mui/icons-material/Fingerprint";
import VendorIcon from "@mui/icons-material/Business";
import VulnerabilitiesIcon from "@mui/icons-material/Flag";
import PeopleIcon from "@mui/icons-material/People";
import DevicesIcon from "@mui/icons-material/Devices";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import CodeIcon from "@mui/icons-material/Code";
import AuditIcon from "@mui/icons-material/FactCheck";
import IntegrationsIcon from "@mui/icons-material/Extension";
import AdminsIcon from "@mui/icons-material/SupervisedUserCircle";
import PoliciesIcon from "@mui/icons-material/HistoryEdu";
import CompanyIcon from "@mui/icons-material/BusinessCenter";
import ControlPointIcon from "@mui/icons-material/Policy";
import FolderIcon from "@mui/icons-material/Folder";
import Logo from "../../Assets/Images/Logo.png";
import { useLocation } from "react-router-dom";
import LocalLibraryOutlinedIcon from "@mui/icons-material/LocalLibraryOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import useIsDemoInstance from "../../pages/Chat/Chat-Hooks/useIsDemoInstance";
import {
  RESTRICTED_SIDEBAR_SECTIONS,
  useRestrictedModuleAccess,
} from "../AccessControl";

const drawerWidth = 260;
const collapsedDrawerWidth = 72;

// Custom hook for all sidebar styles
const useStyles = (theme, drawerOpen, isLoading) => {
  return useMemo(() => {
    // Keep the width strictly driven by the drawerOpen prop (parent computes effective state).
    // But disable transitions while loading so there's no flicker.
    const baseTransition = isLoading
      ? "none"
      : "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)";
    return {
      drawer: {
        width: drawerOpen ? drawerWidth : collapsedDrawerWidth,
        flexShrink: 0,
      },
      drawerPaper: {
        width: drawerOpen ? drawerWidth : collapsedDrawerWidth,
        border: "none",
        background:
          "linear-gradient(180deg, #0A0A23 0%, #1A1A3A 50%, #0F0F2B 100%)",
        color: "#fff",
        transition: baseTransition,
        boxShadow: `8px 0 32px rgba(0, 0, 0, 0.25), inset -1px 0 0 rgba(255, 255, 255, 0.05)`,
        position: "relative",
        overflow: "hidden",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
          radial-gradient(circle at 20% 30%, rgba(120, 119, 198, 0.15) 0%, transparent 50%),
          radial-gradient(circle at 80% 70%, rgba(255, 119, 198, 0.1) 0%, transparent 50%),
          linear-gradient(135deg, rgba(255, 255, 255, 0.02) 0%, transparent 100%)
        `,
          pointerEvents: "none",
        },
      },
      toolbar: {
        display: "flex",
        alignItems: "center",
        justifyContent: " ",
        minHeight: "90px !important",
        position: "sticky",
        top: 0,
        zIndex: 100,
        backdropFilter: "blur(20px)",
        boxShadow:
          "0 4px 20px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
        flexShrink: 0,
      },
      logo: {
        height: 60,
        marginLeft: 1,
        display: drawerOpen ? "block" : "none",
        filter: "drop-shadow(0 4px 12px rgba(0, 0, 0, 0.4)) brightness(1.1)",
        transition: isLoading ? "none" : "all 0.3s ease",
        "&:hover": {
          transform: "scale(1.05)",
          filter: "drop-shadow(0 6px 20px rgba(0, 0, 0, 0.5)) brightness(1.2)",
        },
      },
      collapsedLogo: {
        width: 36,
        height: 36,
        borderRadius: "50%",
        background: "linear-gradient(135deg, #7877C6 0%, #FF77C6 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "16px",
        fontWeight: "bold",
        color: "white",
        boxShadow:
          "0 4px 16px rgba(120, 119, 198, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
        border: "1px solid rgba(255, 255, 255, 0.2)",
      },
      navigationContainer: {
        flex: 1,
        overflowY: "auto",
        overflowX: "hidden",
        padding: "12px 0",
        minHeight: 0,
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          background: "rgba(255, 255, 255, 0.05)",
          borderRadius: "3px",
          margin: "4px 0",
        },
        "&::-webkit-scrollbar-thumb": {
          background:
            "linear-gradient(180deg, rgba(120, 119, 198, 0.6) 0%, rgba(255, 119, 198, 0.6) 100%)",
          borderRadius: "3px",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          "&:hover": {
            background:
              "linear-gradient(180deg, rgba(120, 119, 198, 0.8) 0%, rgba(255, 119, 198, 0.8) 100%)",
            boxShadow: "0 0 8px rgba(120, 119, 198, 0.4)",
          },
        },
        "&::-webkit-scrollbar-corner": {
          background: "transparent",
        },
        scrollbarWidth: "thin",
        scrollbarColor: "rgba(120, 119, 198, 0.6) rgba(255, 255, 255, 0.05)",
      },
      list: {
        padding: "0 10px",
      },
      listItem: (isActive) => ({
        backgroundColor: isActive ? "rgba(120, 119, 198, 0.25)" : "transparent",
        color: isActive ? "#fff" : "rgba(255, 255, 255, 0.85)",
        borderRadius: "8px",
        margin: "3px 0",
        padding: "10px 14px",
        cursor: "pointer",
        transition: baseTransition,
        border: isActive
          ? "1px solid rgba(120, 119, 198, 0.4)"
          : "1px solid transparent",
        boxShadow: isActive
          ? "0 4px 16px rgba(120, 119, 198, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
          : "none",
        "&:hover": {
          backgroundColor: isActive
            ? "rgba(120, 119, 198, 0.3)"
            : "rgba(255, 255, 255, 0.08)",
          transform: "translateX(4px)",
          boxShadow:
            "0 6px 20px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.05)",
        },
        "&::before": isActive
          ? {
              content: '""',
              position: "absolute",
              left: 0,
              top: "50%",
              transform: "translateY(-50%)",
              width: "3px",
              height: "50%",
              background: "linear-gradient(180deg, #7877C6 0%, #FF77C6 100%)",
              borderRadius: "0 2px 2px 0",
              boxShadow: "0 0 8px rgba(120, 119, 198, 0.6)",
            }
          : {},
      }),
      listItemIcon: (isActive) => ({
        color: isActive ? "#fff" : "rgba(255, 255, 255, 0.85)",
        minWidth: "34px",
        fontSize: "1.2rem",
        transition: "all 0.3s ease",
        filter: isActive
          ? "drop-shadow(0 0 4px rgba(255, 255, 255, 0.3))"
          : "none",
        "& .MuiSvgIcon-root": {
          fontSize: "1.2rem",
        },
      }),
      listItemText: (isActive) => ({
        fontSize: "0.875rem",
        fontWeight: isActive ? 600 : 500,
        color: isActive ? "#fff" : "rgba(255, 255, 255, 0.85)",
        textShadow: isActive ? "0 0 8px rgba(255, 255, 255, 0.3)" : "none",
        lineHeight: 1.4,
      }),
      sectionHeader: {
        color: "rgba(255, 255, 255, 0.95)",
        paddingLeft: 2,
        paddingTop: 1.2,
        paddingBottom: 0.6,
        paddingRight: 1,
        fontWeight: 700,
        fontSize: "0.75rem",
        textAlign: "left",
        textTransform: "uppercase",
        letterSpacing: "0.8px",
        position: "relative",
        textShadow: "0 0 8px rgba(255, 255, 255, 0.2)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        cursor: "pointer",
        transition: "all 0.3s ease",
        "&:hover": {
          color: "#fff",
          backgroundColor: "rgba(255, 255, 255, 0.08)",
          borderRadius: "6px",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: 16,
          right: 16,
          height: "1px",
          background:
            "linear-gradient(90deg, transparent 0%, rgba(120, 119, 198, 0.4) 50%, transparent 100%)",
          boxShadow: "0 0 4px rgba(120, 119, 198, 0.3)",
        },
      },
      expandIcon: {
        color: "rgba(255, 255, 255, 0.7)",
        fontSize: "1rem",
        transition: "all 0.3s ease",
        "&:hover": {
          color: "#fff",
        },
      },
    };
  }, [theme, drawerOpen, isLoading]);
};

function getIconByText(text) {
  switch (text) {
    case "Rego AI":
      return <ChatIcon />;
    case "Dashboard":
      return <DashboardIcon />;
    case "Controls":
      return <ControlPointIcon />;
    case "Evidence":
      return <FolderIcon />;
    case "Tasks":
      return <PendingActionsOutlinedIcon />;
    case "Frameworks":
      return <FrameworksIcon />;
    case "Policy Enforcement":
      return <PolicyIcon />;
    case "Documents":
      return <DocumentsIcon />;
    case "Risk Management":
      return <RiskIcon />;
    case "Access Management":
      return <AccessIcon />;
    case "Vendors":
      return <VendorIcon />;
    case "Vulnerabilities":
      return <VulnerabilitiesIcon />;
    case "People":
      return <PeopleIcon />;
    case "Policies":
      return <PoliciesIcon />;
    case "Devices":
      return <DevicesIcon />;
    case "Infrastructure":
      return <DnsOutlinedIcon />;
    case "Code Security":
      return <CodeIcon />;
    case "Audit Logs":
      return <AuditIcon />;
    case "Admins":
      return <AdminsIcon />;
    case "Company Settings":
      return <CompanyIcon />;
    case "Integrations":
      return <IntegrationsIcon />;
    case "Training":
      return <LocalLibraryOutlinedIcon />;
      case "Assets":
        return <DevicesOtherIcon />;
    case "Audits":
      return <AuditIcon />;
    case "Integrated Evidences":
      return <AutoAwesomeIcon />;
    default:
      return null;
  }
}

const Sidebar = ({
  drawerOpen,
  isLoading = false,
  handleTabClick,
  activeTab,
  isEmployeeMode,
  isAuditorMode = false,
  closeSecurityDetails,
  drawerItem,
}) => {
  const location = useLocation();
  const theme = useTheme();
  // Sidebar expects effective drawerOpen from parent (we rely on parent to provide it),
  // and also receives isLoading to disable transitions.
  const styles = useStyles(theme, drawerOpen, isLoading);
  const isDemoInstance = useIsDemoInstance();
  const isRestrictedAccess = useRestrictedModuleAccess();

  // State for collapsible sections - Initialize with all sections expanded
  const [expandedSections, setExpandedSections] = useState(() => {
    const adminDrawerItems = Object.entries(drawerItem?.[0]?.admin[0] || {});
    const initialState = {};
    adminDrawerItems.forEach(([key]) => {
      initialState[key] = true; // Start with all sections expanded
    });
    return initialState;
  });

  const adminDrawerItems = Object.entries(
    isRestrictedAccess
      ? RESTRICTED_SIDEBAR_SECTIONS
      : drawerItem?.[0]?.admin[0] || {}
  ).sort(([, a], [, b]) => a.order - b.order);
  const employeeDrawerItems = Array.isArray(drawerItem?.[1]?.staff)
    ? drawerItem[1]?.staff
    : [];
  const auditorDrawerItems = Array.isArray(drawerItem?.[2]?.auditor)
    ? drawerItem[2]?.auditor
    : [];

  const drawerItems = isEmployeeMode 
    ? employeeDrawerItems 
    : isAuditorMode 
      ? auditorDrawerItems 
      : adminDrawerItems;

  const toggleSection = (sectionKey) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const isItemActive = (item) => {
    const pathSegment = item.toLowerCase().replace(/\s+/g, "-");
    return (
      activeTab === item ||
      location.pathname.endsWith(`/${pathSegment}`) ||
      location.pathname.includes(`/${pathSegment}/`)
    );
  };

  const renderListItem = (item, key) => {
    const isActive = isItemActive(item);

    return (
      <ListItem
        key={key}
        onClick={() => {
          handleTabClick(item);
          closeSecurityDetails();
        }}
        sx={styles.listItem(isActive)}
      >
        <ListItemIcon sx={styles.listItemIcon(isActive)}>
          {getIconByText(item)}
        </ListItemIcon>
        {drawerOpen && (
          <ListItemText
            primary={item}
            primaryTypographyProps={styles.listItemText(isActive)}
          />
        )}
      </ListItem>
    );
  };

  const handleSectionToggle = (sectionKey, event) => {
    event.preventDefault();
    event.stopPropagation();
    toggleSection(sectionKey);
  };

  const effectiveOpen = drawerOpen || isLoading; // keep safe; if parent forgets, use loading as fallback

  return (
    <Drawer
      variant="permanent"
      open={effectiveOpen}
      sx={styles.drawer}
      PaperProps={{
        sx: styles.drawerPaper,
      }}
    >
      {/* Logo Section */}
      <Toolbar sx={styles.toolbar}>
        <Box component="img" src={Logo} alt="Logo" sx={styles.logo} />
        {!effectiveOpen && <Box sx={styles.collapsedLogo}>K</Box>}
      </Toolbar>

      {/* Navigation Content */}
      <Box sx={styles.navigationContainer}>
        <List sx={styles.list}>
          {/* Dashboard, Chat, and Evidence Section */}
          {!isEmployeeMode && !isAuditorMode && !isRestrictedAccess && (
            <>
              {renderListItem("Dashboard", "dashboard")}
              {isDemoInstance && renderListItem("Rego AI", "chat")}
              {isDemoInstance && renderListItem("Evidence", "evidence")}
              {renderListItem("Tasks", "tasks")}
            </>
          )}

          {/* Main Navigation Items */}
          <Box>
            <List>
              {drawerItems &&
                (isEmployeeMode || isAuditorMode
                  ? drawerItems.map((item, idx) => renderListItem(item, idx))
                  : adminDrawerItems.map(([key, { permissionsData }], idx) => {
                      const isExpanded = expandedSections[key] ?? true;

                      return (
                        <React.Fragment key={key}>
                          <Box
                            sx={styles.sectionHeader}
                            onClick={(event) => handleSectionToggle(key, event)}
                          >
                            <span>{key}</span>
                            {drawerOpen && (
                              <Box sx={styles.expandIcon}>
                                {isExpanded ? <ExpandLess /> : <ExpandMore />}
                              </Box>
                            )}
                          </Box>
                          <Collapse
                            in={isExpanded}
                            timeout="auto"
                            unmountOnExit
                          >
                            {permissionsData?.map(
                              (item, index) =>
                                item !== "Admins" &&
                                !(item === "Vendors" && !isDemoInstance) &&
                                renderListItem(item, `${key}-${index}`)
                            )}
                            {isDemoInstance &&
                              key.toLowerCase() === "governance" &&
                              !permissionsData?.includes("Vendors") &&
                              renderListItem("Vendors", `${key}-vendors`)}
                          </Collapse>
                        </React.Fragment>
                      );
                    }))}
            </List>
          </Box>
        </List>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
